import { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import MarketComparisonPanel from './MarketComparisonPanel';
import PositionDetails from './PositionDetails';
import LevelDifferences from './LevelDifferences';
import { Alert, AlertDescription } from "./ui/alert";
import { useCompensationData } from '../hooks/useCompensationData';
import { useChartData, useAdjustmentChartData } from '../hooks/useChartData';
import { shortenPositionName } from '../utils/dataProcessing/positionSorting';

function CompensationDashboard() {
  const {
    files,
    loadedData,
    isLoading,
    error,
    departments,
    handleFileUpload,
    resetAllFiles,
    filterByDepartment,
    getSortedPositions
  } = useCompensationData();
  
  const [selectedDepartment, setSelectedDepartment] = useState('Alle');
  const [selectedView, setSelectedView] = useState('overview');



  

  // Filtere und sortiere Daten
  const filteredData = useMemo(() => {
    return filterByDepartment(selectedDepartment);
  }, [selectedDepartment, filterByDepartment]);

  const sortedPositions = useMemo(() => {
    return getSortedPositions(filteredData);
  }, [filteredData, getSortedPositions]);


  // Verwende Chart Data Hooks
  const chartData = useChartData(sortedPositions);
  const adjustmentChartData = useAdjustmentChartData(chartData);





  // Funktion zum Zurücksetzen mit Department Reset
  function handleResetAllFilesWithDepartment() {
    resetAllFiles();
    setSelectedDepartment('Alle');
  }

  // Render Hauptkomponente
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold">Compensation Dashboard</h1>
            
            {/* Compact File Indicator */}
            {loadedData.length > 0 && (
              <div className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                <span className="text-sm">📊</span>
                <span className="text-sm font-medium">{files.length} Reports</span>
                <button 
                  onClick={handleResetAllFilesWithDepartment}
                  className="text-blue-600 hover:text-blue-800 ml-2"
                  title="Alle Reports entfernen"
                >
                  🗑️
                </button>
              </div>
            )}
          </div>
          
          <div className="flex space-x-2">
            <button 
              onClick={() => setSelectedView('overview')}
              className={`px-3 py-1 rounded ${selectedView === 'overview' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              Übersicht
            </button>
            <button 
              onClick={() => setSelectedView('details')}
              className={`px-3 py-1 rounded ${selectedView === 'details' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              Details
            </button>
            <button 
              onClick={() => setSelectedView('diffs')}
              className={`px-3 py-1 rounded ${selectedView === 'diffs' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              Level-Unterschiede
            </button>
            <button 
              onClick={() => setSelectedView('market')}
              className={`px-3 py-1 rounded ${selectedView === 'market' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              Marktvergleich
            </button>
          </div>
        </div>
        
        {/* Compact File Upload - nur wenn keine Dateien geladen */}
        {loadedData.length === 0 && (
          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">Lade Compensation Reports</h2>
            <input 
              type="file" 
              accept=".json" 
              multiple 
              onChange={handleFileUpload} 
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {isLoading && <p className="text-sm text-blue-600 mt-2">Lade Daten...</p>}
          </div>
        )}
        
        {/* Upload Button für weitere Dateien */}
        {loadedData.length > 0 && (
          <div className="mb-6 flex justify-between items-center">
            <div>
              <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors">
                <span className="mr-2">📁</span>
                <span>Weitere Reports hinzufügen</span>
                <input 
                  type="file" 
                  accept=".json" 
                  multiple 
                  onChange={handleFileUpload} 
                  className="hidden"
                />
              </label>
              {isLoading && <span className="ml-3 text-sm text-blue-600">Lade Daten...</span>}
            </div>
          </div>
        )}
        
        {loadedData.length > 0 && (
          <>
            {/* Filter und Ansichtsoptionen */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
              <h2 className="text-lg font-semibold mb-4">Filter</h2>
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select 
                    value={selectedDepartment} 
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    {departments && departments.length > 0 ? (
                      departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))
                    ) : (
                      <option value="">Keine Abteilungen verfügbar</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Inhalt basierend auf ausgewählter Ansicht */}
            {selectedView === 'overview' && (
              <div className="mb-6 bg-white p-4 rounded-lg shadow">
                <h2 className="text-lg font-semibold mb-4">Gehaltsübersicht (Total Cash): {selectedDepartment}</h2>
                
                {chartData.length > 0 ? (
                  <>
                    <div className="h-96 mb-8">
                      <h3 className="text-md font-medium mb-4">Total Cash Vergleich - Alle Datenquellen</h3>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartData}
                          margin={{ top: 20, right: 30, left: 40, bottom: 40 }}
                          barCategoryGap="20%"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="name" 
                            tick={{ fontSize: 12 }}
                            interval={0}
                            height={60}
                          />
                          <YAxis 
                            label={{ value: 'Total Cash (Tsd. €)', angle: -90, position: 'insideLeft' }}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip 
                            formatter={(value, name) => {
                              const labels = {
                                'current': 'Peerigon Total Cash',
                                'market': 'CP Markt-Median',
                                'kununu': 'Kununu Durchschnitt',
                                'gehaltDe': 'Gehalt.de Durchschnitt'
                              };
                              return [
                                `${value} Tsd. €`, 
                                labels[name] || name
                              ];
                            }}
                            labelFormatter={(label) => {
                              const item = chartData.find(d => d.name === label);
                              return item ? item.fullName : label;
                            }}
                          />
                          <Legend />
                          <Bar dataKey="current" name="Peerigon Total Cash" fill="#4f46e5" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="market" name="CP Markt-Median" fill="#10b981" radius={[2, 2, 0, 0]} />
                          <Bar 
                            dataKey="kununu" 
                            name="Kununu Durchschnitt" 
                            fill="#f59e0b" 
                            radius={[2, 2, 0, 0]}
                          />
                          <Bar 
                            dataKey="gehaltDe" 
                            name="Gehalt.de Durchschnitt" 
                            fill="#ec4899" 
                            radius={[2, 2, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    <div className="h-96">
                      <h3 className="text-md font-medium mb-4">Gehaltsanpassungen nach Quelle (monatlich)</h3>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={adjustmentChartData}
                          margin={{ top: 20, right: 30, left: 40, bottom: 40 }}
                          barCategoryGap="20%"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="name" 
                            tick={{ fontSize: 12 }}
                            interval={0}
                            height={60}
                          />
                          <YAxis 
                            label={{ value: 'Monatliche Anpassung (€)', angle: -90, position: 'insideLeft' }}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip 
                            formatter={(value, name) => {
                              const labels = {
                                'marketAdjustment': 'Compensation Partner',
                                'kununuAdjustment': 'Kununu',
                                'gehaltDeAdjustment': 'Gehalt.de'
                              };
                              return [`${value} €`, labels[name] || name];
                            }}
                            labelFormatter={(label) => {
                              const item = chartData.find(d => d.name === label);
                              return item ? item.fullName : label;
                            }}
                          />
                          <Legend />
                          <Bar dataKey="marketAdjustment" name="Compensation Partner" fill="#10b981" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="kununuAdjustment" name="Kununu" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="gehaltDeAdjustment" name="Gehalt.de" fill="#ec4899" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                ) : (
                  <p>Keine Daten für das ausgewählte Department verfügbar.</p>
                )}
              </div>
            )}
            
            {selectedView === 'details' && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">Positionsdetails: {selectedDepartment}</h2>
                
                <div className="space-y-6">
                  {sortedPositions && sortedPositions.length > 0 ? (
                    sortedPositions.map((position, index) => (
                      <div key={index}>
                        <PositionDetails position={position} />
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>Keine Positionen für diese Abteilung verfügbar</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {selectedView === 'diffs' && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">Level-Unterschiede: {selectedDepartment}</h2>
                <LevelDifferences 
                  sortedPositions={sortedPositions}
                  shortenPositionName={shortenPositionName}
                />
              </div>
            )}
            
            {selectedView === 'market' && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">Marktvergleich: {selectedDepartment}</h2>
                
                {sortedPositions.length > 0 ? (
                  <div className="space-y-6">
                    {sortedPositions.map((position, index) => (
                      <div key={index}>
                        <MarketComparisonPanel position={position} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>Keine Daten für den Marktvergleich verfügbar.</p>
                )}
              </div>
            )}
          </>
        )}
        
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {loadedData.length === 0 && !isLoading && !error && (
          <div className="bg-white p-6 rounded-lg shadow text-center">
            <p className="text-gray-600">Bitte lade Compensation Reports hoch, um die Daten zu analysieren.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompensationDashboard;